import React, { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Search, Home, FolderGit2, User, Mail, Moon, Sun, Globe, Command } from 'lucide-react';
import { useTheme } from '../../core/contexts/ThemeContext';
import { useWindows } from '../../core/contexts/WindowContext';
export function CommandPalette() {
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const {
    theme,
    setTheme,
    language,
    setLanguage,
    t
  } = useTheme();
  const {
    openWindow
  } = useWindows();
  const isLightTheme = theme === 'light';
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setIsOpen(prev => !prev);
      }
      if (e.key === 'Escape' && isOpen) {
        setIsOpen(false);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen]);
  useEffect(() => {
    if (isOpen && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 100);
    } else {
      setQuery('');
    }
  }, [isOpen]);
  const handleAction = (action: () => void) => {
    action();
    setIsOpen(false);
  };
  const actions = [{
    id: 'nav-home',
    label: t('home.title'),
    category: 'Navigation',
    icon: Home,
    action: () => openWindow('home', t('home.title'))
  }, {
    id: 'nav-work',
    label: t('work.title'),
    category: 'Navigation',
    icon: FolderGit2,
    action: () => openWindow('work', t('work.title'))
  }, {
    id: 'nav-about',
    label: t('about.title'),
    category: 'Navigation',
    icon: User,
    action: () => openWindow('about', t('about.title'))
  }, {
    id: 'nav-contact',
    label: t('contact.title'),
    category: 'Navigation',
    icon: Mail,
    action: () => openWindow('contact', t('contact.title'))
  }, {
    id: 'proj-meester',
    label: 'Meester djay',
    category: 'Projects',
    icon: FolderGit2,
    action: () => openWindow('work', t('work.title'), 'meester-djay')
  }, {
    id: 'proj-video',
    label: 'AI Video Generation',
    category: 'Projects',
    icon: FolderGit2,
    action: () => openWindow('work', t('work.title'), 'ai-video')
  }, {
    id: 'proj-bulkr',
    label: 'BULKR App Design',
    category: 'Projects',
    icon: FolderGit2,
    action: () => openWindow('work', t('work.title'), 'bulkr')
  }, {
    id: 'proj-jdg',
    label: 'JDG Advies',
    category: 'Projects',
    icon: FolderGit2,
    action: () => openWindow('work', t('work.title'), 'jdg-advies')
  }, {
    id: 'proj-vvgn',
    label: 'VVGN Platform',
    category: 'Projects',
    icon: FolderGit2,
    action: () => openWindow('work', t('work.title'), 'vvgn-platform')
  }, {
    id: 'set-theme',
    label: isLightTheme ? 'Dark Mode' : 'Light Mode',
    category: 'Settings',
    icon: isLightTheme ? Moon : Sun,
    action: () => setTheme(isLightTheme ? 'dark' : 'light')
  }, {
    id: 'set-lang',
    label: language === 'nl' ? 'Switch to English' : 'Wissel naar Nederlands',
    category: 'Settings',
    icon: Globe,
    action: () => setLanguage(language === 'nl' ? 'en' : 'nl')
  }];
  const filteredActions = actions.filter(action => action.label.toLowerCase().includes(query.toLowerCase()));
  const groupedActions = filteredActions.reduce((acc, action) => {
    if (!acc[action.category]) acc[action.category] = [];
    acc[action.category].push(action);
    return acc;
  }, {} as Record<string, typeof actions>);
  return <AnimatePresence data-id="element-170">
      {isOpen && <div className="fixed inset-0 z-[10000] flex items-start justify-center pt-[15vh] px-4" data-id="element-171">
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2
      }} className={`absolute inset-0 backdrop-blur-sm ${isLightTheme ? 'bg-white/30' : 'bg-black/40'}`} onClick={() => setIsOpen(false)} data-id="element-172" />
          <motion.div initial={{
        opacity: 0,
        scale: 0.95,
        y: -20
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.95,
        y: -20
      }} transition={{
        type: 'spring',
        bounce: 0.2,
        duration: 0.4
      }} className={`relative w-full max-w-lg rounded-2xl overflow-hidden shadow-2xl ${isLightTheme ? 'bg-white/90 border border-black/[0.06] shadow-black/[0.08]' : 'bg-[#1A1A1A]/90 border border-white/[0.06] shadow-black/50'} backdrop-blur-2xl`} data-id="element-173">
            <div className={`flex items-center px-4 py-3 border-b ${isLightTheme ? 'border-black/[0.06]' : 'border-white/[0.06]'}`} data-id="element-174">
              <Search className={`w-5 h-5 mr-3 ${isLightTheme ? 'text-[#6B6B6B]' : 'text-[#9B9B9B]'}`} data-id="element-175" />
              <input ref={inputRef} type="text" placeholder={t('command.placeholder')} value={query} onChange={e => setQuery(e.target.value)} className={`flex-1 bg-transparent border-none outline-none text-base font-medium ${isLightTheme ? 'text-[#1A1A1A] placeholder:text-[#6B6B6B]' : 'text-[#E8E8E6] placeholder:text-[#9B9B9B]'}`} data-id="element-176" />
              <div className={`flex items-center gap-1 px-2 py-1 rounded-md text-[10px] font-mono ${isLightTheme ? 'bg-black/5 text-[#6B6B6B]' : 'bg-white/10 text-[#9B9B9B]'}`} data-id="element-177">
                <Command className="w-3 h-3" data-id="element-178" />
                <span data-id="element-179">K</span>
              </div>
            </div>

            <div className="max-h-[60vh] overflow-y-auto p-2 scrollbar-hide" data-id="element-180">
              {Object.entries(groupedActions).map(([category, items]) => <div key={category} className="mb-4 last:mb-0" data-id="element-181">
                  <div className={`px-3 py-2 text-xs font-mono uppercase tracking-wider ${isLightTheme ? 'text-[#6B6B6B]' : 'text-[#9B9B9B]'}`} data-id="element-182">
                    {category === 'Navigation' ? t('command.navigation') : category === 'Projects' ? t('command.projects') : t('command.settings')}
                  </div>
                  <div className="space-y-1" data-id="element-183">
                    {items.map(action => {
                const Icon = action.icon;
                return <button key={action.id} onClick={() => handleAction(action.action)} className={`w-full flex items-center px-3 py-2.5 rounded-xl transition-colors ${isLightTheme ? 'hover:bg-black/5 text-[#1A1A1A]' : 'hover:bg-white/10 text-[#E8E8E6]'}`} data-id="element-184">
                          <Icon className={`w-4 h-4 mr-3 ${isLightTheme ? 'text-[#6B6B6B]' : 'text-[#9B9B9B]'}`} data-id="element-185" />
                          <span className="text-sm font-medium" data-id="element-186">
                            {action.label}
                          </span>
                        </button>;
              })}
                  </div>
                </div>)}
              {filteredActions.length === 0 && <div className={`px-4 py-8 text-center text-sm ${isLightTheme ? 'text-[#6B6B6B]' : 'text-[#9B9B9B]'}`} data-id="element-187">
                  No results found.
                </div>}
            </div>
          </motion.div>
        </div>}
    </AnimatePresence>;
}